import { useEffect, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Operators() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [dept, setDept] = useState("all");
  const [q, setQ] = useState("");
  const load = () => api.get("/operators").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  useLiveRefresh("masters", load);

  const del = async (o) => {
    if (!window.confirm(`Delete operator "${o.name}" (${o.code})?\n\nHistorical production logs will keep the operator's name for reporting.`)) return;
    try {
      await api.delete(`/operators/${o.id}`);
      toast.success("Operator deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const departments = [...new Set(rows.map((o) => o.department).filter(Boolean))].sort();
  const shown = rows.filter((o) => (dept === "all" || o.department === dept)
    && (!q.trim() || `${o.name} ${o.code}`.toLowerCase().includes(q.trim().toLowerCase())));

  return (
    <div>
      <PageHeader
        subtitle="Shop Floor · Master Data"
        title="Operators"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-operator-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
                <Plus size={16} className="mr-1.5" />Add Operator
              </Button>
            </DialogTrigger>
            <OperatorForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="operators-filter">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code" data-testid="operators-search" className="rounded-sm w-56 h-8 text-sm" />
          {["all", ...departments].map((d) => {
            const n = d === "all" ? rows.length : rows.filter((o) => o.department === d).length;
            return (
              <Button key={d} size="sm" variant={dept === d ? "default" : "outline"} onClick={() => setDept(d)} data-testid={`operators-dept-${d}`}
                className={`rounded-sm h-7 px-2.5 text-xs ${dept === d ? "bg-[#FF5A00] hover:bg-[#e04f00]" : ""}`}>
                {d === "all" ? "All" : d} · {n}
              </Button>
            );
          })}
        </div>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="operators-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Department</th>
                <th className="text-left px-4 py-2.5">Skill</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Hourly Rate</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id} className="border-t border-neutral-100 data-row" data-testid={`operator-row-${o.code}`}>
                  <td className="px-4 py-2.5 font-mono-num">{o.code}</td>
                  <td className="px-4 py-2.5 font-medium">{o.name}</td>
                  <td className="px-4 py-2.5">
                    {o.department || "—"}
                    {o.production === false && <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-400">office / support</span>}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-600">{o.skill || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-sm uppercase tracking-widest ${
                      o.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-700"
                    }`}>{o.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(o.hourly_rate)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => del(o)}
                      data-testid={`del-operator-${o.code}`}
                      className="text-neutral-400 hover:text-rose-700 transition-colors"
                      title="Delete operator"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">{rows.length ? "No one matches." : "No operators yet. Add one, or import the employee list from CSV Import."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OperatorForm({ onDone }) {
  const [f, setF] = useState({ code: "", name: "", skill: "", status: "active", hourly_rate: 0 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/operators", { ...f, hourly_rate: Number(f.hourly_rate) });
      toast.success("Operator added"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display">New Operator</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1.5">Code</label>
          <Input data-testid="operator-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="rounded-sm" required />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Name</label>
          <Input data-testid="operator-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm" required />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Status</label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{["active", "inactive"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Hourly Rate (₹)</label>
          <Input data-testid="operator-rate" type="number" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="rounded-sm" />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Skill / Department</label>
          <Input data-testid="operator-skill" placeholder="e.g. Printing, Slitting, Packing" value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} className="rounded-sm" />
        </div>
        <div className="col-span-2 text-[11px] text-neutral-500 -mt-1">
          <span className="italic">Shift is captured per-log on the Production page (since operator shifts rotate weekly).</span>
        </div>
        <DialogFooter className="col-span-2">
          <Button data-testid="operator-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Add</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
